import { useEffect, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import SuccessHero from '@/shared/assets/svg/success-circular-banner.svg';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModal, ModalContent } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { ConnectAndFundWalletButton } from '@/shared/components/PolkadotConnectionButton/ConnectAndFundWalletButton';
import TokenInputField from '@/shared/components/TokenInputField';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { etherscanUrl } from '@/shared/utils/helpers';
import TokenAmount from '@/shared/utils/TokenAmount';

export interface FundAccountModalProps {
  open: boolean;
  onClose: () => void;
  type: 'liquidity-provider' | 'operator';
  onFunded?: (txHash: string) => void;
}

const learnMoreLink: Record<FundAccountModalProps['type'], string> = {
  'liquidity-provider': 'https://docs.chainflip.io/lp',
  operator: 'https://docs.chainflip.io/validators/delegation',
};

const thingsManaged: Record<FundAccountModalProps['type'], string> = {
  'liquidity-provider': 'positions',
  operator: 'validators',
};

export const FundFlipModalContent = ({ open, onClose, type, onFunded }: FundAccountModalProps) => {
  const { flipBalance, minimumFunding } = useOldFundingSDK();

  const [amount, setAmount] = useState<TokenAmount | undefined>(
    () => minimumFunding?.max(TokenAmount.fromWholeUnits(10)) ?? TokenAmount.fromWholeUnits(10),
  );
  const [fundTxHash, setFundTxHash] = useState<string | null>(null);

  const { selectedAccount: polkadotAccount } = usePolkadot(true);
  const minimumAmount = minimumFunding ?? TokenAmount.fromWholeUnits(10);

  const balanceTokenAmount = TokenAmount.from(flipBalance) ?? undefined;

  useEffect(() => {
    if (!open) setFundTxHash(null);
  }, [open]);

  const fundingForm = (
    <ModalContent
      title={`Fund account with ${FLIP_SYMBOL}`}
      description={
        <>
          This <span className="text-cf-light-4">{FLIP_SYMBOL}</span> is used to pay the gas fees
          required to manage your {thingsManaged[type]}.{' '}
          <Link href={learnMoreLink[type]} target="_blank" className="underline">
            Learn more
          </Link>
        </>
      }
      buttons={
        <>
          <Button size="medium" type="secondary-standard" onClick={onClose}>
            Cancel
          </Button>
          <ConnectAndFundWalletButton
            className="font-aeonikMedium !text-14"
            token={chainflipAssetMap.Flip}
            tokenAmount={amount ?? TokenAmount.ZERO}
            minimumAmount={minimumAmount}
            onFunded={(txHash) => {
              setFundTxHash(txHash);
              onFunded?.(txHash);
            }}
          />
        </>
      }
    >
      <TokenInputField
        token={chainflipAssetMap.Flip}
        value={amount}
        error={amount?.lt(minimumAmount) ? 'Amount is smaller than minimum funding' : undefined}
        onChange={setAmount}
        balance={balanceTokenAmount}
      />
    </ModalContent>
  );

  const successContent = (
    <div className="relative flex flex-col items-center">
      <SuccessHero className="pointer-events-none absolute mt-[-20px]" />
      <div className="flex w-full flex-col items-center space-y-5 pb-8 pt-24 text-white">
        <div className="flex flex-col items-center space-y-1 font-aeonikMedium">
          You funded your account with
          <div className="cf-gray-gradient flex items-center gap-x-2 font-aeonikBold text-[32px]">
            <TokenWithChainByAsset asset="Flip" size="large" /> {amount?.toFormatted()}{' '}
            {FLIP_SYMBOL}
          </div>
          <div>to {abbreviate(polkadotAccount.idSs58)}</div>
        </div>
        <Button onClick={onClose}>Return to Dashboard</Button>
        <Link
          target="_blank"
          href={etherscanUrl('tx', fundTxHash as string)}
          className="flex items-center gap-x-1 text-12 text-cf-light-3"
        >
          <WindowIcon />
          View on explorer
        </Link>
      </div>
    </div>
  );

  return fundTxHash == null ? fundingForm : successContent;
};

export const FundFlipModal = ({
  open,
  onClose,
  ...rest
}: FundAccountModalProps): React.JSX.Element => (
  <DashboardModal open={open} onClose={onClose}>
    <div className="flex w-[487px] flex-col rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
      <button type="button" className="absolute z-10 self-end" onClick={onClose}>
        <CloseIcon />
      </button>
      <FundFlipModalContent open={open} onClose={onClose} {...rest} />
    </div>
  </DashboardModal>
);
